import { useSnackbar } from 'notistack'
import { useRef, useEffect, memo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { SCHOOLS_API } from '../../../../firebase/school-api'
import {
    upsertSchoolEventRead,
    removeSchoolEventRead,
    resetSchoolEventReads,
} from '../../../../redux/schoolEventReadsSlice'
import {
    removeSchoolEvent,
    resetSchoolEvents,
    SchoolEventEntity,
    toSchoolEventEntity,
    upsertSchoolEvent,
    upsertSchoolEvents,
} from '../../../../redux/schoolEventsSlice'
import { selectUserUid } from '../../../../redux/userSlice'
import { logger } from '../../../../helpers/logger'
import {
    SchoolEventSnackbar,
    SchoolEventActions,
} from '../../../../components/Snackbar'
import { sToMs } from '../../../../helpers/utils'
import snackbarStyles from '../../../../components/Snackbar/index.module.scss'

const SchoolEventListener = ({ schoolUid }: { schoolUid: string }) => {
    const initialised = useRef<boolean>(false)
    const { enqueueSnackbar } = useSnackbar()
    const dispatch = useDispatch()

    // Intial load
    useEffect(() => {
        const initalFetch = async () => {
            if (!schoolUid) return
            let ents: SchoolEventEntity[]
            try {
                const data =
                    (await SCHOOLS_API.events.fetchOnce(schoolUid)) || {}
                ents = Object.entries(data).map(toSchoolEventEntity)
            } catch (e) {
                return
            }
            dispatch(upsertSchoolEvents(ents))
            initialised.current = true
        }

        initalFetch()
        return () => {
            initialised.current = false
        }
    }, [schoolUid, dispatch])

    useEffect(() => {
        if (!schoolUid) return
        logger.debug('Attaching school events listener')

        const unsub = SCHOOLS_API.events.listen(schoolUid, {
            added: (snap) => {
                const ent = toSchoolEventEntity([snap.key!, snap.val()])
                dispatch(upsertSchoolEvent(ent))
                if (initialised.current) {
                    enqueueSnackbar(<SchoolEventSnackbar data={ent} />, {
                        anchorOrigin: {
                            vertical: 'top',
                            horizontal: 'center',
                        },
                        className: `${snackbarStyles.notificationBanner} ${snackbarStyles[ent.type]}`,
                        autoHideDuration: sToMs(30),
                        action: (id) => (
                            <SchoolEventActions snackbarId={id} args={ent} />
                        ),
                    })
                }
            },
            changed: (snap) => {
                dispatch(
                    upsertSchoolEvent(
                        toSchoolEventEntity([snap.key!, snap.val()])
                    )
                )
            },
            removed: (snap) => {
                dispatch(removeSchoolEvent(snap.key!))
            },
        })

        return () => {
            logger.debug('Detatching school events listener')
            unsub.forEach((offFn) => offFn())
            dispatch(resetSchoolEvents())
        }
    }, [enqueueSnackbar, dispatch, schoolUid])

    const userUid = useSelector(selectUserUid)
    useEffect(() => {
        if (!schoolUid || !userUid) return

        logger.debug('ATTACH-LISTENER: school event reads')
        const eventReadUnsubs = SCHOOLS_API.eventReads.listen(
            schoolUid,
            userUid,
            {
                added: (snap) =>
                    dispatch(
                        upsertSchoolEventRead({
                            id: snap.key!,
                            status: snap.val(),
                        })
                    ),
                removed: (snap) => dispatch(removeSchoolEventRead(snap.key!)),
            }
        )

        return () => {
            logger.debug('DETACH-LISTENER: school event reads')
            eventReadUnsubs.forEach((offFn) => offFn())
            dispatch(resetSchoolEventReads())
        }
    }, [schoolUid, userUid, dispatch])

    return null
}

export default memo(SchoolEventListener)
